/*
  OCA Search, shared client + header suggestions.

  Talks to the self-hosted Typesense index straight from the browser with a
  public, search-only, scoped key (settings from snippets/oca-search-config).
  window.OcaSearch holds what the suggestions dropdown below and the results
  page (assets/oca-search-page.js) share.

  Request logic mirrors oca-search/src/search/query.ts -- keep them in step.
  Every search sends one multi_search with two variants:
    - "code": the query with spaces and - . / removed, upper-cased, against
      the normalised SKU and part-number fields, prefix on and typos off, so
      39186TJBA30, 39186 TJB A30, 39186-tjb-a30 and 39186TJB all match;
    - "text": the query as typed over titles, vehicles and descriptions with
      typo tolerance and synonyms.
  If the code variant finds anything it is shown, otherwise the text one.
*/
(() => {
  const settingsEl = document.getElementById('oca-search-settings');
  if (!settingsEl || window.OcaSearch) return;
  const settings = JSON.parse(settingsEl.textContent);

  const TEXT_FIELDS = {
    // sku_suffixes: radio codes sit inside SKUs (7BZ0 in Ho7BZ0U).
    query_by: 'sku_norm,part_numbers_norm,sku_suffixes,title,part_numbers,sku,vehicles,description_text',
    query_by_weights: '8,8,6,4,3,3,2,1',
    num_typos: '0,0,0,2,0,0,1,1',
    prefix: 'true,true,true,true,true,true,false,false',
    // Rank by the highest-weight field matched, so SKU and part-number hits
    // outrank the same word in a title or description.
    text_match_type: 'max_weight',
    // Default 1: one exact "Amplifer" in a title would stop typo matching.
    typo_tokens_threshold: 20,
    max_candidates: 20,
  };
  const CODE_FIELDS = {
    query_by: 'sku_norm,part_numbers_norm',
    num_typos: 0,
    prefix: true,
    drop_tokens_threshold: 0,
    // Default 4: 39186TJB must reach every 39186TJB* code, not just one product's.
    max_candidates: 200,
  };

  /** Display names for part_types facet values. */
  const PART_TYPE_LABELS = {
    radio: 'Radios',
    amplifier: 'Amplifiers',
    display: 'Displays',
    module: 'Modules',
    'control panel': 'Control panels',
    dvd: 'DVD',
    remote: 'Remotes',
    headphones: 'Headphones',
    'cd changer': 'CD changers',
    navigation: 'Navigation',
    'map disc': 'Map discs & cards',
    'climate control': 'Climate controls',
    speaker: 'Speakers',
    antenna: 'Antennas',
    camera: 'Cameras',
    'adapter / harness': 'Adapters & harnesses',
    'cd mechanism': 'CD mechanisms',
    'repair service': 'Repair services',
    other: 'Other',
  };
  const partTypeLabel = (v) => PART_TYPE_LABELS[v] || v.charAt(0).toUpperCase() + v.slice(1);
  /**
   * A part word from elsewhere in the theme (the hero finder's Part options,
   * the category bar) as a part_types value: "radio", "Radios", "headphone"
   * and "DVD" all resolve. Null when the word is not a part type.
   */
  const partTypeFor = (term) => {
    const t = String(term || '').trim().toLowerCase();
    if (!t) return null;
    for (const [value, label] of Object.entries(PART_TYPE_LABELS)) {
      if (t === value || t === value + 's' || t + 's' === value || t === label.toLowerCase()) return value;
    }
    return null;
  };

  const compact = (q) => q.replace(/[\s\-./]+/g, '').toUpperCase();
  const looksLikeCode = (q) => {
    const c = compact(q);
    return c.length >= 4 && /\d/.test(c) && q.trim().split(/\s+/).length <= 4 && /^[A-Z0-9]+$/.test(c);
  };
  const esc = (s) =>
    String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const money = new Intl.NumberFormat(settings.locale || undefined, { style: 'currency', currency: settings.currency || 'USD' });

  /**
   * Query fields for one variant; callers add filters, facets, paging.
   * Stock plays no part in the order: a sold-out part ranks like any other,
   * since customers can ask for a special order. Ties go to the newest.
   */
  function variantParams(variant, q) {
    q = q.trim();
    if (variant === 'code') {
      return { collection: settings.collection, q: compact(q), ...CODE_FIELDS, sort_by: '_text_match:desc,created_at:desc' };
    }
    return {
      collection: settings.collection,
      q: q || '*',
      ...TEXT_FIELDS,
      sort_by: q ? '_text_match(buckets: 10):desc,created_at:desc' : 'created_at:desc',
    };
  }

  /*
    Outage handling. A request that fails, or takes more than 4 s, marks the
    search server as down for this tab for 2 minutes (sessionStorage, so the
    next page knows too). While it is down, searches go to the fallback:
    Shopify's own search (/search?view=native, always Dawn's page) or, for a
    vehicle from the finder, EasySearch's own vehicle page.
  */
  const DOWN_KEY = 'oca-search-down';
  const DOWN_FOR = 2 * 60_000;
  const markDown = () => {
    try {
      sessionStorage.setItem(DOWN_KEY, String(Date.now()));
    } catch (e) {}
  };
  const isDown = () => {
    try {
      return Date.now() - Number(sessionStorage.getItem(DOWN_KEY) || 0) < DOWN_FOR;
    } catch (e) {
      return false;
    }
  };

  /** Where to send a shopper when the search server is down. */
  function fallbackHref({ q = '', vehicleTag = '', words = '' } = {}) {
    if (vehicleTag) return `${(settings.allProductsUrl || '/collections/all').replace(/\/$/, '')}/${encodeURIComponent(vehicleTag)}`;
    const terms = [q, words].filter(Boolean).join(' ').trim();
    if (!terms) return settings.allProductsUrl || '/collections/all';
    const url = new URL(settings.searchUrl || '/search', location.origin);
    url.search = new URLSearchParams({ view: 'native', type: 'product', 'options[prefix]': 'last', q: terms }).toString();
    return url.pathname + url.search;
  }

  const cache = new Map();
  function multiSearch(searches) {
    const body = JSON.stringify({ searches });
    if (cache.has(body)) return cache.get(body);
    const host = /^https?:\/\//.test(settings.host) ? settings.host.replace(/\/$/, '') : `https://${settings.host}`;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 4000);
    // API key in the query string and a text/plain body keep this a "simple"
    // CORS request: no preflight round trip per keystroke.
    const promise = fetch(`${host}/multi_search?x-typesense-api-key=${encodeURIComponent(settings.key)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body,
      signal: controller.signal,
    })
      .then((r) => {
        if (!r.ok) throw new Error(`Search failed: HTTP ${r.status}`);
        return r.json();
      })
      .catch((err) => {
        markDown();
        throw err;
      })
      .finally(() => clearTimeout(timer));
    cache.set(body, promise);
    promise.catch(() => cache.delete(body));
    if (cache.size > 80) cache.delete(cache.keys().next().value);
    return promise;
  }

  /** Results page URL with q and any extra params (type, model, ymm...). */
  function resultsHref(params) {
    const url = new URL(settings.resultsUrl || '/search', location.origin);
    for (const [k, v] of Object.entries(params)) if (v) url.searchParams.set(k, v);
    return url.pathname + url.search;
  }

  // routes.root_url is "/" or a locale prefix like "/fr".
  const productHref = (d) => `${(settings.root || '/').replace(/\/$/, '')}/products/${encodeURIComponent(d.handle)}`;

  /** The part number that matched: prefix on the compact query, else Typesense's highlight. */
  function matchedPartNumber(hit, compactQ) {
    const d = hit.document;
    if (!d.part_numbers || !d.part_numbers.length) return '';
    if (compactQ) return d.part_numbers.find((pn) => compact(pn).startsWith(compactQ)) || '';
    const hl = (hit.highlights || []).find((x) => x.field === 'part_numbers');
    if (hl && hl.matched_tokens && hl.matched_tokens.length) return d.part_numbers[(hl.indices || [0])[0]] || '';
    return '';
  }

  /** Title with Typesense's <mark>s kept and everything else escaped. */
  function titleHtml(hit) {
    const hl = (hit.highlights || []).find((x) => x.field === 'title');
    return hl && hl.snippet ? esc(hl.snippet).replace(/&lt;(\/?)mark&gt;/g, '<$1mark>') : esc(hit.document.title);
  }

  window.OcaSearch = {
    settings, compact, looksLikeCode, esc, money, variantParams, multiSearch,
    resultsHref, productHref, matchedPartNumber, titleHtml, partTypeLabel, partTypeFor,
    isDown, fallbackHref,
  };

  /* ---------------- Header suggestions ---------------- */

  const SUGGEST_FIELDS = 'id,handle,title,sku,price,available,image,part_numbers';
  const PHONE = window.matchMedia('(max-width: 749px)');

  class OcaSearchSuggest extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="search"]');
      this.panel = this.querySelector('[data-oca-suggest-panel]');
      this.body = this.querySelector('[data-oca-suggest-body]');
      this.resetButton = this.querySelector('button[type="reset"]');
      this.seq = 0;
      this.activeIndex = -1;
      this.lastQuery = null;

      let timer;
      this.input.addEventListener('input', () => {
        this.resetButton && this.resetButton.classList.toggle('hidden', !this.input.value);
        clearTimeout(timer);
        timer = setTimeout(() => this.search(), 100);
      });
      this.input.addEventListener('focus', () => {
        if (PHONE.matches) this.open();
        else if (this.input.value.trim().length >= 2) this.search();
      });
      this.input.addEventListener('keydown', (e) => this.onKeydown(e));
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        const active = this.options()[this.activeIndex];
        if (active && !active.matches('.oca-suggest__all')) return active.click();
        this.showAll();
      });
      // "See all results" too: searched in place on the results page.
      this.body.addEventListener('click', (e) => {
        if (!e.target.closest('.oca-suggest__all') || !document.querySelector('oca-search-page')) return;
        e.preventDefault();
        this.showAll();
      });
      this.form.addEventListener('reset', () => {
        setTimeout(() => {
          this.resetButton && this.resetButton.classList.add('hidden');
          this.render(null);
          this.input.focus();
        });
      });
      this.querySelector('[data-oca-suggest-close]').addEventListener('click', () => this.close());
      document.addEventListener('pointerdown', (e) => {
        if (this.classList.contains('is-open') && !PHONE.matches && !this.contains(e.target)) this.close();
      });
      this.addEventListener('focusout', (e) => {
        if (!PHONE.matches && e.relatedTarget && !this.contains(e.relatedTarget)) this.close();
      });
      PHONE.addEventListener('change', () => this.close());
    }

    /**
     * The full results for what is typed. On the results page this bar is the
     * page's search box: it searches in place, keeping the shopper's vehicle
     * and in-stock filters. Anywhere else it goes to the results page. While
     * the search server is down it goes to Shopify's own search instead.
     */
    showAll() {
      const q = this.input.value.trim();
      if (isDown()) {
        location.href = fallbackHref({ q });
        return;
      }
      const page = document.querySelector('oca-search-page');
      if (page && typeof page.search === 'function') {
        this.close();
        this.input.blur();
        page.search(q);
        return;
      }
      location.href = resultsHref({ q });
    }

    open() {
      if (this.classList.contains('is-open')) return;
      this.classList.add('is-open');
      this.input.setAttribute('aria-expanded', 'true');
      if (PHONE.matches) document.documentElement.classList.add('oca-search-lock');
      if (this.input.value.trim().length >= 2) this.search();
      else if (PHONE.matches) this.render(null);
    }

    close() {
      this.classList.remove('is-open');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      document.documentElement.classList.remove('oca-search-lock');
      this.activeIndex = -1;
      if (PHONE.matches) this.input.blur();
    }

    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) {
        this.lastQuery = null;
        if (PHONE.matches) this.render(null);
        else this.close();
        return;
      }
      if (q === this.lastQuery && this.body.childElementCount) return this.open();
      // Server down: no suggestions; Enter still works (showAll falls back).
      if (isDown()) return this.close();
      const seq = ++this.seq;
      const code = looksLikeCode(q);
      const common = { per_page: 6, include_fields: SUGGEST_FIELDS, highlight_fields: 'title,part_numbers' };
      const searches = [
        ...(code ? [{ ...variantParams('code', q), ...common }] : []),
        { ...variantParams('text', q), ...common, facet_by: 'part_types,models', max_facet_values: 4 },
      ];
      this.classList.add('is-loading');
      let data;
      try {
        data = await multiSearch(searches);
      } catch (err) {
        console.error(err);
        if (seq === this.seq) {
          this.classList.remove('is-loading');
          // Desktop: just no dropdown. Phone panel: say so, Enter still searches.
          if (PHONE.matches) this.body.innerHTML = '<p class="oca-suggest__hint">Suggestions are unavailable right now. Press search to see results.</p>';
          else this.close();
        }
        return;
      }
      if (seq !== this.seq) return; // a newer keystroke won
      this.classList.remove('is-loading');
      const [first, second] = data.results;
      const codeHit = code && first.found > 0;
      this.lastQuery = q;
      this.render({ q, codeMode: codeHit, result: codeHit ? first : code ? second : first });
      this.open();
    }

    render(view) {
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
      if (!view) {
        this.body.innerHTML = PHONE.matches
          ? '<p class="oca-suggest__hint">Search by part number, SKU, or vehicle and part, like “2014 Camry radio”.</p>'
          : '';
        return;
      }
      const { q, codeMode, result } = view;
      const hits = result.hits || [];
      const id = this.panel.id;
      if (!result.found) {
        this.body.innerHTML = `<p class="oca-suggest__empty">No products match “${esc(q)}”. Try a part number with or without dashes, or fewer words.</p>`;
        return;
      }

      let links = '';
      if (!codeMode) {
        const facet = (name) => ((result.facet_counts || []).find((f) => f.field_name === name) || { counts: [] }).counts;
        const types = facet('part_types').filter((c) => c.value !== 'other').slice(0, 3);
        const models = facet('models').slice(0, 3);
        const chips = [
          ...types.map((c) => [resultsHref({ q, type: c.value }), `${partTypeLabel(c.value)}`, c.count]),
          ...models.map((c) => [resultsHref({ q, model: c.value }), c.value, c.count]),
        ];
        if (chips.length) {
          links = `<div class="oca-suggest__links" role="group" aria-label="Narrow your search">${chips
            .map(([href, label, n], i) => `<a href="${esc(href)}" class="oca-suggest__link" role="option" id="${id}-l${i}">${esc(label)} <span>${n}</span></a>`)
            .join('')}</div>`;
        }
      }

      const compactQ = codeMode ? compact(q) : '';
      const items = hits
        .map((h, i) => {
          const d = h.document;
          const pn = matchedPartNumber(h, compactQ);
          return `<li><a href="${esc(productHref(d))}" class="oca-suggest__item" role="option" id="${id}-p${i}">
  <span class="oca-suggest__thumb">${d.image ? `<img src="${esc(d.image)}" alt="" width="64" height="64" loading="lazy">` : ''}</span>
  <span class="oca-suggest__text">
    <span class="oca-suggest__title">${titleHtml(h)}</span>
    <span class="oca-suggest__meta">${d.sku ? `<span>${esc(d.sku)}</span>` : ''}${pn ? `<span>Part # <mark>${esc(pn)}</mark></span>` : ''}</span>
  </span>
  <span class="oca-suggest__price">${money.format(d.price)}${d.available ? '' : `<span class="oca-suggest__soldout">${esc(settings.soldOutLabel || 'Sold out')}</span>`}</span>
</a></li>`;
        })
        .join('');

      this.body.innerHTML = `${links}
<p class="oca-suggest__heading">${codeMode ? 'Part number matches' : 'Products'}</p>
<ul class="oca-suggest__items" role="listbox" aria-label="Products">${items}</ul>
<a href="${esc(resultsHref({ q }))}" class="oca-suggest__all" role="option" id="${id}-all">See all ${result.found.toLocaleString()} result${result.found === 1 ? '' : 's'} for “${esc(q)}”</a>`;
    }

    options() {
      return [...this.body.querySelectorAll('[role="option"]')];
    }

    onKeydown(e) {
      const opts = this.options();
      if (e.key === 'Escape') {
        if (this.classList.contains('is-open')) {
          e.preventDefault();
          this.close();
        }
        return;
      }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      if (!opts.length) return;
      e.preventDefault();
      this.open();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      this.activeIndex = this.activeIndex + step;
      if (this.activeIndex >= opts.length) this.activeIndex = -1;
      if (this.activeIndex < -1) this.activeIndex = opts.length - 1;
      opts.forEach((o, i) => o.classList.toggle('is-active', i === this.activeIndex));
      const active = opts[this.activeIndex];
      if (active) {
        this.input.setAttribute('aria-activedescendant', active.id);
        active.scrollIntoView({ block: 'nearest' });
      } else {
        this.input.removeAttribute('aria-activedescendant');
      }
    }
  }

  if (!customElements.get('oca-search-suggest')) customElements.define('oca-search-suggest', OcaSearchSuggest);
})();
